import React, { useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { css } from '@linaria/core';
import { styled } from '@linaria/react';
import PropTypes from 'prop-types';

import { createStorageDecorator } from '@blocks/_common/decorators/storageDecorator';
import {
  changeLoginAbility,
  clearSubmitError,
  removeLogin,
  setLogin,
  setUserRole,
  toggleGdpr,
} from '@blocks/_common/mutators';
import { useOrderStore } from '@blocks/stores/order';
import { createLogout, useSessionStore } from '@blocks/stores/session';
import { SubmitConfig } from '@blocks/types';
import withErrorBoundary from '@blocks/utils/withErrorBoundary';

import { OAuth2, onOAuth2LoginHandler } from '../_common';
import { genRecaptchaToken, getSession, getUserCheckAuth, navigate, postSession } from '../api';
import { DOMAIN_DEFAULT, GDPR_PROPS_DEFAULT, USER_ROLE_CUSTOMER } from '../constants';
import providers from '../constants/auth-providers';
import gaConfig from '../constants/ga-config';
import { useIsAuthorized, useOnSubmit } from '../hooks';
import { createFocusDecorator } from '../order/decorators';
import { Button, Link, Row, When } from '../ui';
import { Spinner } from '../ui/icon';
import { and, apipe, debug, getRoute, handleErrors, is, isNot, sideEffect, when } from '../utils';
import { handleGaLogout, handleGaSignIn } from '../utils/gtm/gtm-events';

import Gdpr from './components/gdpr';
import Login from './components/login';
import Password from './components/password';
import Recaptcha from './components/recaptcha';

import '../styles/global.css';

const initialValues = {
  login: '',
  password: '',
  stay_signed_in: true,
  policy_agree: false,
  marketing_agree: false,
  showGdpr: false,
  user_role: USER_ROLE_CUSTOMER,
};

type AuthFormValues = typeof initialValues;

const onSubmit = (config: SubmitConfig) => (values: AuthFormValues) => {
  const { recaptcha_site_key, utc_offset_min, domain, onSignIn, setLoading } = config || {};
  return apipe(
    debug(
      sideEffect(() => setLoading(true)),
      'order: loading on',
    ),
    when(is('password', 'login'), debug(getUserCheckAuth, 'login: getUserCheckAuth')),
    when(is('login_ability'), debug(genRecaptchaToken, 'login: genRecaptchaToken')),
    when(is('login_ability', 'captcha_token'), debug(postSession, 'login: postSession')),
    when(is('auth_token'), debug(getSession, 'login: getSession')),
    when(
      is('auth_token'),
      debug((state: unknown) => {
        useOrderStore.getState().clearLogin();
        return state;
      }, 'login: clearStoredLogin'),
    ),
    when(is('auth_token'), debug(getRoute, 'login: getRoute')),
    when(is('auth_token'), debug(sideEffect(onSignIn), 'login: ga4 onSignIn')),
    when(and(is('auth_token'), isNot('remote')), debug(navigate, 'login: navigate')),
    when(
      isNot('auth_token'),
      debug(
        sideEffect(() => setLoading(false)),
        'order: loading off',
      ),
    ),
    debug(handleErrors, 'login: handleErrors'),
  )({
    ...values,
    recaptcha_site_key,
    recaptcha_action: 'POST/users/sessions',
    utc_offset_min,
    domain,
    route: '',
  });
};

const subscription = {
  submitting: true,
  values: false,
  validating: true,
  submitSucceeded: true,
  submitFailed: true,
  submitError: true,
  hasSubmitErrors: true,
  dirtySinceLastSubmit: true,
};

const AuthForm = styled.form`
  width: 100%;
`;

const AuthFields = styled.div``;

const SubmitSpinner = styled.div`
  display: flex;
  justify-content: center;
  & svg {
    fill: var(--bb-field-placeholder);
  }
`;

const AuthLinks = styled.div`
  padding-top: 24px;
  margin-top: 24px;
  border-top: 1px solid #efefef;
  display: flex;
  justify-content: center;
  gap: 12px;
  align-items: center;
  padding-bottom: 8px;
`;

const NoAccountText = styled.span`
  color: var(--bb-field-color);
`;

const registrationBoldLink = css`
  font-weight: bold;
`;

export type AuthProps = {
  domain?: string;
  registrationLink?: string;
  registrationLabel?: string;
  forgotPasswordLink?: string;
  forgotPasswordLabel?: string;
  submitText?: string;
  submitTextAuthorized?: string;
  name?: string;
  remote?: boolean;
  onSignIn?: (props: unknown) => void;
  termsLink?: string;
  termsLabel?: string;
  policyLink?: string;
  policyLabel?: string;
  termLinkWriter?: string;
  policyLinkWriter?: string;
  debugOAuth?: boolean;
  debugOAuthProviderUsed?: string;
  enableOAuthInitializationPreview?: boolean;
  redirectUrl?: string;
  withIcon?: boolean;
  hideAccountPrompt?: boolean;
};

const Auth: React.FC<AuthProps> = ({
  domain = DOMAIN_DEFAULT,
  registrationLink = '/registration.html',
  registrationLabel = 'Sign up',
  forgotPasswordLink = '/auth_password_reset.html',
  forgotPasswordLabel = 'Forgot password',
  submitText = 'Login',
  submitTextAuthorized = 'Continue to dashboard',
  name = 'auth',
  remote = false,
  onSignIn = undefined,
  termsLink = GDPR_PROPS_DEFAULT.termsLink,
  termsLabel = GDPR_PROPS_DEFAULT.termsLabel,
  policyLink = GDPR_PROPS_DEFAULT.policyLink,
  policyLabel = GDPR_PROPS_DEFAULT.policyLabel,
  termLinkWriter = GDPR_PROPS_DEFAULT.termLinkWriter,
  policyLinkWriter = GDPR_PROPS_DEFAULT.policyLinkWriter,
  debugOAuth = false,
  debugOAuthProviderUsed = '',
  enableOAuthInitializationPreview = false,
  redirectUrl,
  withIcon,
  hideAccountPrompt,
}) => {
  const {
    session: { dashboard_home_url_path },
  } = useSessionStore(domain);

  const authenticated = useIsAuthorized(domain);

  const [loading, setLoading] = useState(false);
  const submitHandler = useOnSubmit<AuthFormValues>({
    domain,
    onSubmit,
    onSignIn: (props: unknown) => {
      handleGaSignIn(gaConfig, { method: 'default' })(props);
      // modal
      onSignIn?.(props);
    },
    setLoading,
    remote,
  });

  const logoutHandler = React.useCallback(
    () =>
      createLogout({
        domain,
        onLogout: handleGaLogout(gaConfig),
      }),
    [domain],
  );

  /**
   * Disable redirect if you need
   */
  useEffect(() => {
    if (authenticated) {
      window.location.href = redirectUrl || `/dashboard${dashboard_home_url_path}orders/`;
    }
  }, [authenticated, dashboard_home_url_path, redirectUrl]);

  return (
    <>
      <Form
        initialValues={{ ...initialValues, remote, name }}
        decorators={[createFocusDecorator, createStorageDecorator]}
        mutators={{
          toggleGdpr,
          clearSubmitError,
          setUserRole,
          removeLogin,
          changeLoginAbility,
          setLogin,
        }}
        onSubmit={submitHandler}
        subscription={{ ...subscription }}
        render={({ handleSubmit, submitting }) => (
          <AuthForm
            onSubmit={handleSubmit}
            data-bb-auth
            name={name}
          >
            <AuthFields>
              {!authenticated && (
                <OAuth2
                  domain={domain}
                  source_context="authorization"
                  handler={onOAuth2LoginHandler}
                  providers={providers}
                  debugOAuth={debugOAuth}
                  debugOAuthProviderUsed={debugOAuthProviderUsed}
                  enableOAuthInitializationPreview={enableOAuthInitializationPreview}
                />
              )}

              <Row>
                <Login
                  withIcon={withIcon}
                  domain={domain}
                  remote={remote}
                  authenticated={authenticated}
                  logoutHandler={logoutHandler}
                />
              </Row>
              <Row>
                <Password
                  withIcon={withIcon}
                  forgotPasswordLink={forgotPasswordLink}
                  forgotPasswordLabel={forgotPasswordLabel}
                  authenticated={authenticated}
                />
              </Row>

              <When
                field="showGdpr"
                is
              >
                <Row>
                  <Gdpr
                    {...{
                      termsLink,
                      termsLabel,
                      policyLink,
                      policyLabel,
                      termLinkWriter,
                      policyLinkWriter,
                    }}
                  />
                </Row>
              </When>

              <Row>
                {authenticated && (
                  <a href={`${dashboard_home_url_path}orders/`}>
                    <Button type="button">
                      {submitting || loading ? (
                        <SubmitSpinner>
                          <Spinner
                            width="19.5"
                            height="19.5"
                          />
                        </SubmitSpinner>
                      ) : (
                        submitTextAuthorized
                      )}
                    </Button>
                  </a>
                )}
                {!authenticated && (
                  <Button
                    disabled={submitting || loading}
                    type="submit"
                  >
                    {submitting || loading ? (
                      <SubmitSpinner>
                        <Spinner
                          width="19.5"
                          height="19.5"
                        />
                      </SubmitSpinner>
                    ) : (
                      submitText
                    )}
                  </Button>
                )}
              </Row>

              {!authenticated && <Recaptcha />}
            </AuthFields>

            {!hideAccountPrompt && !authenticated && (
              <AuthLinks>
                <NoAccountText>Don’t have an account?</NoAccountText>
                <Link
                  className={registrationBoldLink}
                  href={registrationLink}
                >
                  {registrationLabel}
                </Link>
              </AuthLinks>
            )}
          </AuthForm>
        )}
      />
    </>
  );
};

Auth.propTypes = {
  domain: PropTypes.string,
  registrationLink: PropTypes.string,
  registrationLabel: PropTypes.string,
  forgotPasswordLink: PropTypes.string,
  forgotPasswordLabel: PropTypes.string,
  submitText: PropTypes.string,
  submitTextAuthorized: PropTypes.string,
  name: PropTypes.string,
  remote: PropTypes.bool,
  onSignIn: PropTypes.func,
  termsLink: PropTypes.string.isRequired,
  termsLabel: PropTypes.string.isRequired,
  policyLink: PropTypes.string.isRequired,
  policyLabel: PropTypes.string.isRequired,
  termLinkWriter: PropTypes.string.isRequired,
  policyLinkWriter: PropTypes.string.isRequired,
  debugOAuth: PropTypes.bool,
  debugOAuthProviderUsed: PropTypes.string,
  enableOAuthInitializationPreview: PropTypes.bool,
  hideAccountPrompt: PropTypes.bool,
};

export default withErrorBoundary(Auth);
